import { IConfiguration } from '../../../../domain/conf'
import { Utils } from '../../../../utils/common'
import { logger } from '../../../../utils/common/logger'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { IdentityEvent, IIdentityEvent } from '../../../common/events/identity'
import { IProfile, Profile } from '../../../common/profile'
import { IIdentityFormFields, IIdentityServiceAdapter } from '../adapter'
import { EmailValidationWidget } from './widgets/email-validation-widget'
import { ForgotPasswordWidget } from './widgets/forgot-password-widget'
import { LoginWidget } from './widgets/login-widget'
import { RegistrationWidget } from './widgets/registration-widget'
import { ResetPasswordWidget } from './widgets/reset-password-widget'
import { SocialLoginWidget } from './widgets/social-login-widget'

import { LocalStore } from '../../../../utils/storage/local'
import { IdentityService } from '../../index'
import { PasswordRequirementsList } from './modules/password-requirements-list'
import { ShowPasswordButton } from './modules/show-password-button'
import { LoginRadiusApiClient } from './services/api-client'
import { LoginService } from './services/login-service'

interface IWindow {
    LoginRadiusV2: any,
    location: Location

    addEventListener(type: string, cb: () => void): void
}

declare let window: IWindow

const COUNTRY = 'Canada'
const MILLISECONDS_TO_SECONDS = 1000

// Login radius error codes
// Reference: https://www.loginradius.com/docs/api/v2/getting-started/response-codes/customer-identity-api-codes/
export enum LRErrorCode {
    LINK_ALREADY_USED = 974,
    LINK_EXPIRED = 975,
    LINK_TOKEN_INVALID = 1063,
    NEW_PASSWORD_INVALID = 1015,
    INVAlID_USER_PASSWORD = 966,
    USER_DOES_NOT_EXIST = 938,
    EMAIL_ADDRESS_TAKEN = 936
}

export enum LRErrorMessage {
    NEW_PASSWORD_INVALID =
        'Your new password is too similar to your old passwords. Please provide a new password.',
    INVAlID_USER_PASSWORD =
        'The email address and/or password you entered does not match any NewsConnect account. Please try again.',
    USER_DOES_NOT_EXIST =
        'The email address and/or password you entered does not match any NewsConnect account. Please try again.',
    EMAIL_ADDRESS_TAKEN =
        'This email is already registered. To log in to this account please try signing in or resetting your password.'
}

export interface ILoginRadiusOptions {
    apiKey: string,
    appName: string,
    debugMode?: boolean,
    forgotPasswordUrl?: string,
    formValidationMessage?: boolean,
    integrationName: string,
    sott?: string,
    tokenType: string,
    verificationUrl: string,
    hashTemplate?: boolean,
    accessTokenResponse?: boolean,
    callbackType?: string,
}

export interface IJwt {
    aud?: string
    exp?: number // Token expire date
    first_name?: string
    last_name?: string
}

export class LoginRadiusAdapter implements IIdentityServiceAdapter {
    public static DOM = {
        class: {
            emailValidationContainer: 'email-validation-container',
            errorMessageContainer: 'identity-form__message--error',
            identityForm: 'identity-form',
            inputErrorMessage: 'form-container__message--error',
            inputWrapperError: 'form-container__field--error',
            profileShortcode: 'postmedia-profile-shortcode',
            setPasswordPage: 'page-set-password'
        },
        id: {
            forgotPasswordContainer: 'forgotpassword-container',
            loginContainer: 'login-container',
            registrationContainer: 'registration-container',
            resetPasswordContainer: 'resetpassword-container',
            socialLoginContainer: 'social-login-container',
            socialLoginTemplate: 'sociallogintemplate'
        }
    }
    public static ssoRedirectLink: string
    public apiKey: string
    public appName: string
    public lrObject: any
    protected lrOptions: ILoginRadiusOptions
    private lrApiClient: LoginRadiusApiClient
    private registrationWidget: RegistrationWidget
    private emailValidationWidget: EmailValidationWidget
    private loginWidget: LoginWidget
    private socialLoginWidget: SocialLoginWidget
    private forgotPasswordWidget: ForgotPasswordWidget
    private resetPasswordWidget: ResetPasswordWidget

    constructor(protected conf: IConfiguration) {
        this.apiKey = (conf.identity.sso.hasOwnProperty('loginradius')) ?
            conf.identity.sso.loginradius.apiKey : ''
        this.appName = (conf.identity.sso.hasOwnProperty('loginradius')) ?
            conf.identity.sso.loginradius.appName : ''
        const webSiteOrigin = location.origin

        this.lrOptions = {
            accessTokenResponse: true,
            apiKey: this.apiKey,
            appName: this.appName,
            callbackType: 'QueryString',
            debugMode: __NON_PROD,
            forgotPasswordUrl: `${webSiteOrigin}/reset-password/`,
            hashTemplate: true,
            integrationName: 'news-websites',
            tokenType: 'jwt',
            verificationUrl: `${webSiteOrigin}/validate-email/`
        }

        // tslint:disable-next-line: max-line-length
        LoginRadiusAdapter.ssoRedirectLink = `https://${this.appName}.hub.loginradius.com/ssologin/setSafariToken?apiKey=${this.apiKey}`
    }

    /**
     * Deletes stored items in localStorage related to identity (LoginRadius and Viafoura token keys)
     */
    public static DeleteIdentityLocalStorage() {
        // check for JWT in localStorage
        if (!localStorage.getItem(Store.JWT) && !localStorage.getItem(Store.LR_TOKEN_KEY)) {
            // if not found, already removed and reloaded - no need to reload again (prevent viafoura signout loop)
            return
        }

        localStorage.removeItem(Store.LR_TOKEN_KEY)
        localStorage.removeItem(Store.JWT)
        // Safari - delete stored viafoura localStorage items for full viafoura sign out
        if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
            localStorage.removeItem(Store.VF_ACCESS)
            localStorage.removeItem(Store.VF_REFRESH)
        }
    }

    public async init(): Promise<void> {
        if (window.LoginRadiusV2) {
            const isSetPasswordPage = !!document.querySelector(`.${LoginRadiusAdapter.DOM.class.setPasswordPage}`)

            if (document.querySelector(`#${LoginRadiusAdapter.DOM.id.registrationContainer}`)) {
                try {
                    await this.setSOTT()
                } catch (e) {
                    logger('error', 'Error setting SOTT: ', e)
                    return
                }
            }

            this.lrObject = new window.LoginRadiusV2(this.lrOptions)
            logger('info', `LoginRadius object created`)

            // Add hooks first, then initialize widgets
            // Reference: https://www.loginradius.com/docs/libraries/js-libraries/javascript-hooks
            this.lrObject.$hooks.register('afterFormRender', this.afterFormRenders)

            // Custom button label
            this.lrObject.$hooks.call('setButtonsName', {
                forgotpassword: 'Send Email',
                login: 'Sign In',
                registration: 'Create Account',
                resetPassword: (isSetPasswordPage) ? 'Set Password' : 'Reset Password'
            })

            // Custom field label
            this.lrObject.$hooks.call('customizeFormLabel', {
                emailid: 'Email Address'
            })

            // Custom field placeholder
            this.lrObject.$hooks.call('customizeFormPlaceholder', {
                emailid: 'youremail@emailaddress.com',
                firstname: 'John',
                lastname: 'Smith'
            })

            // Custom field attributes
            this.lrObject.$hooks.call('formAttributes', {
                confirmpassword: 'required',
                emailid: 'required&type=email',
                firstname: 'required',
                lastname: 'required',
                password: 'required'
            })

            // Custom error messages
            this.lrObject.$hooks.call('mapErrorMessages', [
                {
                    code: LRErrorCode.NEW_PASSWORD_INVALID,
                    description: LRErrorMessage.NEW_PASSWORD_INVALID,
                    message: LRErrorMessage.NEW_PASSWORD_INVALID
                },
                {
                    code: LRErrorCode.INVAlID_USER_PASSWORD,
                    description: LRErrorMessage.INVAlID_USER_PASSWORD,
                    message: LRErrorMessage.INVAlID_USER_PASSWORD
                },
                {
                    code: LRErrorCode.USER_DOES_NOT_EXIST,
                    description: LRErrorMessage.USER_DOES_NOT_EXIST,
                    message: LRErrorMessage.USER_DOES_NOT_EXIST
                },
                {
                    code: LRErrorCode.EMAIL_ADDRESS_TAKEN,
                    description: LRErrorMessage.EMAIL_ADDRESS_TAKEN,
                    message: LRErrorMessage.EMAIL_ADDRESS_TAKEN
                }
            ])

            logger('info', 'LoginRadius hooks added')

            this.registrationWidget = new RegistrationWidget(
                LoginRadiusAdapter.DOM.id.registrationContainer, this.lrObject)
            this.loginWidget = new LoginWidget(LoginRadiusAdapter.DOM.id.loginContainer, this.lrObject)
            this.forgotPasswordWidget = new ForgotPasswordWidget(
                LoginRadiusAdapter.DOM.id.forgotPasswordContainer, this.lrObject)
            this.resetPasswordWidget = new ResetPasswordWidget(
                LoginRadiusAdapter.DOM.id.resetPasswordContainer, this.lrObject
            )
            this.emailValidationWidget = new EmailValidationWidget(
                LoginRadiusAdapter.DOM.class.emailValidationContainer, this.lrObject)
            this.socialLoginWidget = new SocialLoginWidget(
                LoginRadiusAdapter.DOM.id.socialLoginContainer, this.lrObject,
                LoginRadiusAdapter.DOM.id.socialLoginTemplate)

            logger('debug', `Initializing LoginRadius UI`)
            this.registrationWidget.initForm()
            this.loginWidget.initForm()
            this.forgotPasswordWidget.initForm()
            this.resetPasswordWidget.initForm()
            this.socialLoginWidget.initForm()
            this.emailValidationWidget.initEmailValidation()
            logger('debug', 'LoginRadius Widgets Initialized')

            const mod = await import('../buttons')
            // @ts-ignore
            mod.IdentityButtons.initDOM(this.conf.page.siteName)

            document.dispatchEvent(Utils.event(IdentityEvent.LOGIN_MODAL_READY))
            if (localStorage) {
                await this.initializeSSO()
                await this.lrTokenFromUrl()
                await this.initializeLoginState()
            }

            document.dispatchEvent(Utils.event<IIdentityEvent<boolean>, IdentityEvent, boolean>(
                IdentityEvent.SESSION_INIT,
                this.hasSession()))

        } else {
            logger('error', `LoginRadiusV2 object not found. Please make sure LR script is loaded`)
        }
    }

    /**
     * request a new Secure One Time Token from LoginRadius
     * The SOTT is used by LoginRadius to authenticate the user registration calls
     *
     * reference: https://www.loginradius.com/docs/api/v2/customer-identity-api/sott-usage/
     * reference: https://www.loginradius.com/docs/api/v2/customer-identity-api/session/generate-sott-token/
     */
    public async setSOTT() {
        const response = await fetch(location.origin + '/api-root/subscription/identity/get_sott/', {
            headers: {
                'content-type': 'application/json'
            },
            method: 'GET'
        })
        const jsonResponse = await response.json()

        if (jsonResponse.hasOwnProperty('Sott')) {
            this.lrOptions.sott = jsonResponse.Sott
        } else {
            logger('error', `Error fetching SOTT ${response}`)
        }
    }

    /**
     * Looks for SSO token in hosted solution and sets it to local storage.
     * Only works when third-party cookies are enabled
     * https://www.loginradius.com/docs/single-sign-on/tutorial/web-sso/overview/
     */
    public async initializeSSO() {
        if (this.hasSession()) {
            logger('info', `SSO already initialized, SSO token - ${this.getAccessToken()}`)
            return
        }
        // tslint:disable-next-line: max-line-length
        const ssoTokenCall = await fetch(`https://${this.appName}.hub.loginradius.com/ssologin/login`, {
            credentials: 'include', // includes third party cookies
            headers: {
                'content-type': 'application/json'
            },
            method: 'GET'
        })

        const ssoTokenCallRes = await ssoTokenCall.json()

        if (ssoTokenCallRes.isauthenticated) {
            const ssoToken = ssoTokenCallRes.token
            localStorage.setItem(Store.LR_TOKEN_KEY, ssoToken)
            localStorage.removeItem(Store.JWT)
            logger('info', `SSO token found - ${ssoToken}`)
        }
    }

    /**
     * Sets LR token from the current URL if exists
     *
     */

    public async lrTokenFromUrl() {
        const urlParams = Utils.urlParams(document.location)
        if (urlParams.has('lr-token')) {
            localStorage.setItem(Store.LR_TOKEN_KEY, urlParams.get('lr-token'))
        }
    }

    /**
     * Return stored token
     */
    public getAccessToken(): string | undefined {
        if (this.hasSession()) {
            return localStorage.getItem(Store.LR_TOKEN_KEY)
        }
        return undefined
    }

    /**
     * Get redirect URL and navigate to sign in page.
     * Set current URL to session storage to be redirected from the login widget
     * @param url - set custom redirect_url in sessionStorage instead of current url
     */
    public signIn(url?: string) {
        const redirectUrl = url ? url : location.href
        if (sessionStorage) {
            sessionStorage.setItem('redirect_url', redirectUrl)
            sessionStorage.setItem(Store.GNS_REDIRECT_URL, redirectUrl)
            sessionStorage.removeItem(Store.ACTIVATE_BUTTON)
        }
        location.assign(`${location.origin}/sign-in/`)
    }

    /**
     * Remove local storage profile and LR access keys
     * Reload page
     */
    public async signOut() {
        const profileService = document.querySelector(`.${LoginRadiusAdapter.DOM.class.profileShortcode}`)

        if (localStorage) {
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, this.emptyProfile()))
            LoginRadiusAdapter.DeleteIdentityLocalStorage()
            // send Logout Analytics event
            LoginService.sendLogoutAnalytics()
        }

        try {
            if (this.lrApiClient) {
                await this.lrApiClient.invalidateAccessToken()
            }
        } catch(e) {
            logger('error', 'Unable to invalidate access token - ', e)
        }

        if (profileService) {
            location.assign('/')
        } else {
            location.reload()
        }
    }

    /**
     * Checks for profile data and LoginRadius access token for session
     * TODO: This should be SSO
     */
    public hasSession() {
        return (localStorage && !!localStorage.getItem(Store.ID) && !!localStorage.getItem(Store.LR_TOKEN_KEY))
    }

    /**
     * Initialize profile and let UI know if we are logged in.
     * Called from constructor
     * May need to capture JWT in this process as well
     */
    public async initializeLoginState() {
        let profileData = {}
        const lrTokenKey = localStorage.getItem(Store.LR_TOKEN_KEY)
        const jwtToken = localStorage.getItem(Store.JWT)

        this.lrApiClient = new LoginRadiusApiClient(this.lrOptions.apiKey,
            this.lrOptions.integrationName, lrTokenKey, this.appName)
        if (lrTokenKey) {
            const isTokenValid = await this.lrApiClient.validateAccessToken()

            if (isTokenValid) {
                try {
                    const profile = await this.lrApiClient.getProfile()
                    const firstName = profile.FirstName
                    const lastName = profile.LastName

                    profileData = {
                        created: profile.CreatedDate,
                        doNotContact: undefined,
                        email: profile.Email[0].Value,
                        emailConfirmed: profile.EmailVerified,
                        familyName: lastName,
                        gender: profile.Gender,
                        givenName: firstName,
                        lastSignIn: profile.LastLoginDate,
                        name: profile.FullName,
                        uuid: profile.Uid,
                    } as IProfile

                    if (!jwtToken || !this.isJwtValid(jwtToken, firstName, lastName)) {
                        const jwt = await this.lrApiClient.getJwtToken()
                        localStorage.setItem(Store.JWT, jwt)
                    }

                    logger('info', 'Access token found: ', lrTokenKey)
                } catch (e) {
                    // Unable to load the profile - user is not logged in
                    profileData = this.emptyProfile()

                    LoginRadiusAdapter.DeleteIdentityLocalStorage()
                    logger('error', 'Unable to load profile or get JWT - ', e)
                }
            } else {
                // Invalid or expired token - user is not logged in
                profileData = this.emptyProfile()

                LoginRadiusAdapter.DeleteIdentityLocalStorage()
                logger('info', 'Invalid or expired token')
            }
        } else {
            // Annoymous user - user is not logged in
            profileData = this.emptyProfile()

            LoginRadiusAdapter.DeleteIdentityLocalStorage()
            logger('info', 'Access token not found')
        }
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_PROFILE_INIT, profileData))
    }

    /**
     * Returns true if token has not expired, aud value is postmedia-user and the fields match the profile
     *
     * @param jwtToken string
     * @param firstName string
     * @param lastName string
     * @return boolean
     */
    public isJwtValid(jwtToken: string, firstName: string, lastName: string): boolean {
        const AUD_VALUE = 'postmedia-user'
        const decodedToken = JSON.parse(atob(jwtToken.split('.')[1])) as IJwt
        const currentTime = Math.floor(Date.now() / MILLISECONDS_TO_SECONDS)
        return decodedToken.exp > currentTime && decodedToken.aud === AUD_VALUE &&
            decodedToken.first_name === firstName && decodedToken.last_name === lastName
    }

    /**
     * Update the account profile
     *
     * @param updateData IIdentityFormFields
     * @return any
     */
    public async updateAccountProfile(updateData: IIdentityFormFields) {
        const response = await this.lrApiClient.updateProfileByToken({
            Addresses: [
                {
                    Address1: updateData.streetAddress,
                    Address2: updateData.address2,
                    City: updateData.city,
                    Country: COUNTRY,
                    PostalCode: updateData.postalCode,
                    State: updateData.province,
                    Type: 'Primary'
                }
            ],
            PhoneNumbers: [
                {
                    PhoneNumber: updateData.phoneNumber,
                    Type: 'Primary'
                }
            ]
        })

        return response
    }

    /**
     * After form renders hook
     *
     * Reference: https://www.loginradius.com/docs/libraries/js-libraries/javascript-hooks/#afterformrenderhook7
     *
     * @param name - Name of the JS widget. Eg: login, registration
     * @param container - ID of the form container
     */
    private async afterFormRenders(name: string, container: string) {
        // tslint:disable-next-line: max-line-length
        const input = document.querySelectorAll(`#${container} input:not([type=submit]):not([type=reset]):not([type=button]):not([type=checkbox]):not([type=hidden])`)
        const checkbox = document.querySelectorAll(`#${container} [type=checkbox]`)
        const submitButton = document.querySelector(`#${container} [type=submit]`)
        const form = document.querySelector(`#${container} form`)

        if (name === 'registration') {
            // tslint:disable-next-line: max-line-length
            const loginradiusInput = document.querySelector('#loginradius-registration-cf_BrandName') as HTMLInputElement
            if (loginradiusInput) {
                loginradiusInput.value = IdentityService.DOM.property
            }
        }

        if (name === 'login' && submitButton) {
            // Add forgot password link
            const row = document.createElement('div')
            DOMUtils.addClass(row, 'identity__link-container')

            const forgotPasswordLink = document.createElement('a')
            DOMUtils.addClass(forgotPasswordLink, 'identity__link')
            forgotPasswordLink.href = '/forgot-password'
            forgotPasswordLink.innerHTML = 'Forgot Password?'
            row.appendChild(forgotPasswordLink)

            submitButton.before(row)
        }

        input.forEach((el: HTMLInputElement) => {
            const inputDiv = el.parentElement
            const label = inputDiv.querySelector('label')
            // input element classes
            DOMUtils.addClass(el, 'form__control')
            DOMUtils.addClass(el, 'col-xs-12')

            // input parent div class
            DOMUtils.addClass(inputDiv, 'form-container__field')

            // label class
            if (label) {
                DOMUtils.addClass(label, 'form__label')
            }
        })

        checkbox.forEach((el: HTMLInputElement) => {
            // checkbox element class
            DOMUtils.addClass(el, 'form__control')

            // checkbox parent div class
            DOMUtils.addClass(el.parentElement, 'form__label--checkbox')
        })

        DOMUtils.addClass(submitButton, 'button')
        DOMUtils.addClass(submitButton, 'button--secondary')
        DOMUtils.addClass(submitButton, 'button--span')

        // add autocomplete and novalidate to form element
        if (form) {
            form.setAttribute('autocomplete', 'on')
            form.setAttribute('novalidate', '')
        }

        // add show/hide password button
        await ShowPasswordButton.initDOM()

        // show password requirements list
        if (document.querySelector(`#${LoginRadiusAdapter.DOM.id.registrationContainer}`) ||
            document.querySelector(`#${LoginRadiusAdapter.DOM.id.resetPasswordContainer}`)) {
            await PasswordRequirementsList.initDOM()
        }

        // show the form
        DOMUtils.addClass(document.body, 'identity-screen-form--active')
    }

    /**
     * Return an empty user profile
     */
    private emptyProfile() {
        const localProfile = LocalStore.get<IProfile>(Store.ID)
        const anonymous: IProfile = new Profile()

        if (localProfile) {
            anonymous.id = localProfile.id
        }

        return anonymous
    }
}
